// Importing types from react
import React, { ChangeEvent, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoopIcon from '@mui/icons-material/Loop';
import { Alert, Button, Grid, Snackbar, Typography, useMediaQuery } from '@mui/material';
import { useAuth } from 'context/Auth/AuthContext';
import { Form, Formik, FormikHelpers } from 'formik';
import routes from 'routes';
import { GridItem } from 'components';
import MFAInput from 'components/Form/MFAInput';
import { getInitialValues, validationSchema } from './constants';
import { StyledForgotPasswordButton, StyledLink, StyledSimpleText } from 'pages/Authentication/SignIn/styled';
import theme, { animations } from 'themes';
import { InitialValues } from 'features/Authentication/VerifyCode/types';

// Main component
const VerifyCodeAmplify: React.FC = () => {
  const { verifyCode, verifyMFA, resendCode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const itemsRef = useRef<(HTMLInputElement | null)[]>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showError, setShowError] = useState<boolean>(false);
  const [showResent, setShowResent] = useState<boolean>(false);

  const initialValues = getInitialValues(location.state?.username);

  const handleSubmit = async (
    values: InitialValues,
    { setSubmitting }: FormikHelpers<InitialValues>
  ) => {
    try {
      const code = values.code.join('');
      if (location.state?.mode === 'SMS_MFA') {
        await verifyMFA(code, location.state?.mode);
        navigate('/search');
      } else {
        await verifyCode(values.email, code);
        navigate('/authentication/login');
      }
    } catch (err) {
      setShowError(true);
      setSubmitting(false);
    }
  };

  const handleResendCode = () => {
    resendCode(initialValues.email);
    setShowResent(true);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
          setFieldValue,
        }) => (
          <Form>
            <Grid container gap='10px'>
              <Grid item>
                <Typography textAlign={'center'}>Use the most recently sent code. It may be in your spam folder.</Typography>
              </Grid>
              <GridItem label='Code' required>
                <MFAInput
                  // Pass the props individually or modify MFAInput to accept a form prop
                  setFieldValue={setFieldValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>, index: number) => {
                    setFieldValue(`code[${index}]`, e.target.value);
                    if (index < 5 && itemsRef.current[index + 1]) {
                      // Add a null check before calling focus
                      itemsRef.current[index + 1]?.focus();
                    }
                  }}
                />
              </GridItem>
              <Grid item md={12}>
                <Button
                  fullWidth
                  size='large'
                  type='submit'
                  variant='contained'
                  disabled={isSubmitting}
                  sx={{ mt: 2, mb: 2, textTransform: 'capitalize' }}
                >
                  {isSubmitting && <LoopIcon sx={{ animation: `${animations.spin} 2s linear infinite` }} />}
                  Verify
                </Button>
                <Button
                  fullWidth
                  size='large'
                  onClick={handleResendCode}
                  disabled={isSubmitting}
                  sx={{ mt: 2, mb: 2, textTransform: 'capitalize' }}
                >
                  Resend Code
                </Button>
                {isMobile ? (
                  <StyledForgotPasswordButton
                    size='large'
                    fullWidth
                    variant='contained'
                    onClick={() => navigate(routes.auth.logIn)}
                  >
                    Go back
                  </StyledForgotPasswordButton>
                ) : (
                  <StyledSimpleText>
                    Want to use another account?
                    <StyledLink href={routes.auth.logIn}>Login Here</StyledLink>
                  </StyledSimpleText>
                )}
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>

      <Snackbar open={showError} autoHideDuration={4000} onClose={() => setShowError(false)}>
        <Alert onClose={() => setShowError(false)} severity='error' sx={{ width: '100%' }}>
          Code did not match. Use the most recently sent code. It may be in your spam.
        </Alert>
      </Snackbar>
      <Snackbar open={showResent} autoHideDuration={4000} onClose={() => setShowError(false)}>
        <Alert onClose={() => setShowResent(false)} severity='success' sx={{ width: '100%' }}>
          Code has been resent to your email.
        </Alert>
      </Snackbar>
    </>
  );
};

export default VerifyCodeAmplify;